import buildFilterGroup from 'core/util/filters/buildFilterGroup';
import { getOutboundFilters } from 'app/components/filters/simple/simpleFilterUtils';
import {
  getClassificationFilterValue,
  OttClassificationProjectedValues
} from '@kentik/ui-shared/ott/ottClassification';
import { getCdnFilterGroup } from '../cdn/queries';

export const OTT_LANDING_TOPX = 8;

const baseFilters = {
  baseAgg: [
    {
      filterField: 'service_type',
      operator: '<>',
      filterValue: ''
    }
  ],

  noProjected: [
    {
      filterField: 'ott_classification_type',
      operator: '<>',
      filterValue: OttClassificationProjectedValues.join(',')
    }
  ],

  knownProviders: [
    {
      filterField: 'service_provider',
      operator: '<>',
      filterValue: 'unknown'
    }
  ],

  knownCategories: [
    {
      filterField: 'service_type',
      operator: '<>',
      filterValue: 'unknown'
    }
  ],

  noDstCdn: buildFilterGroup({
    name: 'noDstCdn',
    filters: [
      {
        filterField: 'dst_cdn',
        operator: '=',
        filterValue: ''
      }
    ]
  }),

  no128: buildFilterGroup({
    name: 'no128',
    filters: [
      {
        filterField: 'inet_src|dst_addr',
        operator: '<>',
        filterValue: '::/128'
      }
    ]
  })
};

const getBaseQuery = ({ lookback_seconds = 604800, aggregateType = 'max_bits_per_sec' }) => ({
  all_devices: false,
  device_types: ['router', 'kprobe', 'kappa'],
  fastData: 'Auto',
  show_overlay: false,
  show_total_overlay: false,
  aggregateTypes: [aggregateType],
  lookback_seconds,
  from_to_lookback: lookback_seconds,
  topx: 300,
  depth: 300
});

const getBaseAggQuery = ({
  filters = [],
  lookback_seconds,
  aggregateType,
  allowProjected = false,
  includeBaseAggFilter = true,
  extraFilterGroups = []
}) => ({
  ...getBaseQuery({ lookback_seconds, aggregateType }),

  filters: {
    connector: 'All',
    filterGroups: [
      getCdnFilterGroup({ moduleName: 'ott' }),
      baseFilters.noDstCdn,
      baseFilters.no128,
      ...extraFilterGroups,
      buildFilterGroup({
        filters: getOutboundFilters(filters)
          .concat(includeBaseAggFilter ? baseFilters.baseAgg : [])
          .concat(!allowProjected ? baseFilters.noProjected : [])
      })
    ]
  }
});

const getBaseServiceTypeQuery = ({
  serviceType,
  filters = [],
  lookback_seconds,
  aggregateType,
  allowProjected = false
}) => ({
  ...getBaseQuery({ lookback_seconds, aggregateType }),

  filters: {
    connector: 'All',
    filterGroups: [
      getCdnFilterGroup({ moduleName: 'ott' }),
      baseFilters.noDstCdn,
      baseFilters.no128,
      buildFilterGroup({
        filters: getOutboundFilters(filters)
          .concat([
            {
              filterField: 'service_type',
              operator: '=',
              filterValue: serviceType
            }
          ])
          .concat(!allowProjected ? baseFilters.noProjected : [])
      })
    ]
  }
});

const getBaseProviderQuery = ({ provider, filters = [], lookback_seconds, aggregateType, allowProjected = false }) => ({
  ...getBaseQuery({ lookback_seconds, aggregateType }),

  filters: {
    connector: 'All',
    filterGroups: [
      getCdnFilterGroup({ moduleName: 'ott' }),
      baseFilters.noDstCdn,
      baseFilters.no128,
      buildFilterGroup({
        filters: getOutboundFilters(filters)
          .concat([
            {
              filterField: 'service_provider',
              operator: '=',
              filterValue: provider
            }
          ])
          .concat(!allowProjected ? baseFilters.noProjected : [])
      })
    ]
  }
});

const getBaseServiceQuery = ({ service, filters = [], lookback_seconds, aggregateType, allowProjected = false }) => ({
  ...getBaseQuery({ lookback_seconds, aggregateType }),

  filters: {
    connector: 'All',
    filterGroups: [
      getCdnFilterGroup({ moduleName: 'ott' }),
      baseFilters.noDstCdn,
      baseFilters.no128,
      buildFilterGroup({
        filters: getOutboundFilters(filters)
          .concat([
            {
              filterField: 'service_name',
              operator: '=',
              filterValue: service
            }
          ])
          .concat(!allowProjected ? baseFilters.noProjected : [])
      })
    ]
  }
});

const getLookbackValues = (lookback_seconds) => {
  const lookbackValues = { lookback_seconds, period_over_period: true };

  if (lookback_seconds < 60) {
    lookbackValues.period_over_period_lookback = 1;
    lookbackValues.period_over_period_lookback_unit = 'month';
  } else {
    lookbackValues.period_over_period_lookback = Math.ceil(lookback_seconds / 3600);
    lookbackValues.period_over_period_lookback_unit = 'hour';
  }

  return lookbackValues;
};

export const getCategoryQuery = ({ filters = [], lookback_seconds, aggregateType, allowProjected = false }) => ({
  ...getBaseAggQuery({
    filters: filters.concat(baseFilters.knownCategories),
    lookback_seconds,
    aggregateType,
    allowProjected
  }),
  ...getLookbackValues(lookback_seconds),
  metric: ['service_type'],
  topx: OTT_LANDING_TOPX,
  depth: OTT_LANDING_TOPX * 6, // to compare rows that fall in and out of topx
  chunkSize: 30 * 24 * 3600
});

export const getProvidersQuery = ({ filters = [], lookback_seconds, aggregateType, allowProjected = false }) => ({
  ...getBaseAggQuery({
    filters: filters.concat(baseFilters.knownProviders),
    lookback_seconds,
    aggregateType,
    allowProjected
  }),
  ...getLookbackValues(lookback_seconds),
  metric: ['service_provider'],
  topx: OTT_LANDING_TOPX,
  depth: OTT_LANDING_TOPX * 6, // to compare rows that fall in and out of topx
  chunkSize: 30 * 24 * 3600
});

export const getConnectivityQuery = ({ provider, service, filters = [], lookback_seconds, allowProjected = false }) => {
  const baseQuery = provider
    ? getBaseProviderQuery({ provider, filters, lookback_seconds, allowProjected })
    : getBaseServiceQuery({ service, filters, lookback_seconds, allowProjected });

  return {
    ...baseQuery,
    metric: ['i_src_connect_type_name'],
    aggregateTypes: ['max_bits_per_sec'],
    show_total_overlay: true,
    chunkSize: 30 * 24 * 3600
  };
};

// Top services per category
export const getCategoryServicesQuery =
  ({ filters = [], lookback_seconds, aggregateType, allowProjected = false }) =>
  (serviceType) => ({
    ...getBaseServiceTypeQuery({ serviceType, filters, lookback_seconds, aggregateType, allowProjected }),
    metric: ['service_name'],
    depth: 6
  });

//  Top services per provider
export const getProviderServicesQuery =
  ({ filters = [], lookback_seconds, aggregateType, allowProjected = false }) =>
  (provider) => ({
    ...getBaseProviderQuery({ provider, filters, lookback_seconds, aggregateType, allowProjected }),
    metric: ['service_name'],
    depth: 6
  });

export const getServicesQuery = ({
  filters = [],
  lookback_seconds,
  aggregateType,
  classificationTypes,
  allowProjected
}) => ({
  ...getBaseAggQuery({
    filters,
    lookback_seconds,
    aggregateType,
    includeBaseAggFilter: classificationTypes.length === 0,
    allowProjected,
    extraFilterGroups:
      classificationTypes.length > 0
        ? [
            buildFilterGroup({
              name: 'Classifications',
              named: true,
              connector: 'Any',
              filters: [
                {
                  filterField: 'ott_classification_type',
                  operator: '=',
                  filterValue: getClassificationFilterValue(classificationTypes, allowProjected)
                }
              ]
            })
          ]
        : []
  }),

  metric: ['service_name', 'service_type', 'service_provider', 'ott_classification_type'],
  aggregateTypes: ['max_bits_per_sec', 'max_dst_ip', 'max_bytes_per_dst_ip'],
  outsort: 'max_bits_per_sec',
  topx: 20,
  depth: 20,
  chunkSize: 30 * 24 * 3600
});

export const getTopServicesQuery = ({
  filters = [],
  lookback_seconds,
  aggregateType,
  allowProjected = false,
  ...rest
}) => ({
  ...getBaseAggQuery({ filters, lookback_seconds, aggregateType, allowProjected }),
  metric: ['service_name'],
  show_total_overlay: true,
  viz_type: 'table',
  ...rest
});

export const getServiceRankingsByTypeQuery = ({
  serviceType,
  filters = [],
  lookback_seconds,
  aggregateType,
  allowProjected = false,
  ...rest
}) => ({
  ...getBaseServiceTypeQuery({ serviceType, filters, lookback_seconds, aggregateType, allowProjected }),
  metric: ['service_name'],
  show_total_overlay: true,
  viz_type: 'table',
  topx: 0,
  depth: 8,
  chunkSize: 30 * 24 * 3600,
  ...rest
});

export const getTopServicesByProviderQuery = ({
  provider,
  filters = [],
  lookback_seconds,
  aggregateType,
  allowProjected = false,
  ...rest
}) => ({
  ...getBaseProviderQuery({ provider, filters, lookback_seconds, aggregateType, allowProjected }),
  metric: ['service_name'],
  show_total_overlay: true,
  viz_type: 'table',
  topx: 0,
  depth: 8,
  chunkSize: 30 * 24 * 3600,
  ...rest
});

export const getServiceTypeTrafficQuery = ({
  serviceType,
  provider,
  filters = [],
  lookback_seconds,
  aggregateType,
  allowProjected = false
}) => {
  const baseQuery = provider
    ? getBaseProviderQuery({ provider, filters, lookback_seconds, aggregateType, allowProjected })
    : getBaseServiceTypeQuery({ serviceType, filters, lookback_seconds, aggregateType, allowProjected });

  return {
    ...baseQuery,
    show_total_overlay: false,
    metric: ['service_name', 'service_provider', 'service_type', 'i_src_connect_type_name'],
    outsort: 'max_bits_per_sec'
  };
};

export const getProviderToSiteQuery = ({
  provider,
  filters = [],
  lookback_seconds,
  aggregateType,
  allowProjected = false
}) => ({
  ...getBaseProviderQuery({ provider, filters, lookback_seconds, aggregateType, allowProjected }),
  metric: [
    'ott_classification_type',
    'service_name',
    'i_device_site_name',
    'i_src_connect_type_name',
    'i_src_provider_classification',
    'src_cdn',
    'AS_src'
  ],
  show_total_overlay: false,
  viz_type: 'sankey',
  depth: 20
});

export const getServiceBreakdownQuery = ({
  service,
  metric = 'i_device_site_name',
  filters = [],
  lookback_seconds,
  aggregateType,
  allowProjected = false
}) => ({
  ...getBaseServiceQuery({ service, filters, lookback_seconds, aggregateType, allowProjected }),
  viz_type: 'stackedArea',
  metric: [metric],
  show_total_overlay: true,
  depth: 20
});

export const getServiceToSiteQuery = ({
  service,
  filters = [],
  lookback_seconds,
  aggregateType,
  allowProjected = false
}) => ({
  ...getBaseServiceQuery({ service, filters, lookback_seconds, aggregateType, allowProjected }),
  metric: [
    'ott_classification_type',
    'i_device_site_name',
    'i_src_connect_type_name',
    'i_src_provider_classification',
    'src_cdn',
    'AS_src'
  ],
  show_total_overlay: false,
  viz_type: 'sankey',
  depth: 20
});

export const getClassificationQuery = ({ filters = [], lookback_seconds }) => ({
  ...getBaseQuery({ lookback_seconds }),

  metric: ['ott_classification_type'],
  aggregateTypes: ['max_bits_per_sec'],
  show_total_overlay: true,
  topx: 100,
  chunkSize: 30 * 24 * 3600,

  filters: {
    connector: 'All',
    filterGroups: [
      getCdnFilterGroup({ moduleName: 'ott' }),
      baseFilters.noDstCdn,
      baseFilters.no128,
      buildFilterGroup({
        filters: getOutboundFilters(filters)
      })
    ]
  }
});

export const getTopSubscribershipQuery = ({ serviceType, filters = [], lookback_seconds, allowProjected = false }) => ({
  ...getBaseAggQuery({
    filters: filters.concat([
      {
        filterField: 'service_type',
        operator: '=',
        filterValue: serviceType
      }
    ]),
    lookback_seconds,
    allowProjected
  }),
  viz_type: 'table',
  metric: ['service_name'],
  aggregateTypes: ['p95th_dst_ip'],
  outsort: 'p95th_dst_ip',
  topx: 100,
  depth: 100,
  fastData: 'Full'
});

export const getServiceSubscribershipQuery = ({ filters = [], service, lookback_seconds, allowProjected = false }) => ({
  ...getBaseAggQuery({
    filters: filters.concat({ filterField: 'service_name', operator: '=', filterValue: service }),
    aggregateType: 'max_dst_ip',
    lookback_seconds,
    allowProjected
  }),
  ...getLookbackValues(lookback_seconds),
  viz_type: 'table',
  metric: ['service_name'],
  depth: 1,
  fastData: 'Full'
});

export const getSubscribershipQuery = ({
  topService,
  otherService,
  service,
  serviceType,
  filters = [],
  lookback_seconds,
  allowProjected = false
}) => {
  let reAggInterval;
  const filterGroups = [
    buildFilterGroup({
      name: `${serviceType} Leader: ${topService}`,
      named: true,
      filters: [
        {
          filterField: 'service_name',
          operator: '=',
          filterValue: topService
        }
      ]
    }),
    buildFilterGroup({
      name: `Total ${serviceType}`,
      named: true,
      filters: [
        {
          filterField: 'service_type',
          operator: '=',
          filterValue: serviceType
        }
      ]
    })
  ];

  if (otherService) {
    filterGroups.push(
      buildFilterGroup({
        name: `${topService === service ? `${serviceType} #2` : 'Current'}: ${otherService}`,
        named: true,
        filters: [
          {
            filterField: 'service_name',
            operator: '=',
            filterValue: otherService
          }
        ]
      })
    );
  }

  switch (lookback_seconds) {
    case 3600: // 1 Hour - 5 minute interval
      reAggInterval = 5 * 60;
      break;
    case 172800: // 2 Day - 2 hour interval
      reAggInterval = 2 * 3600;
      break;
    case 259200: // 3 Day - 3 hour interval
      reAggInterval = 3 * 3600;
      break;
    case 604800: // >= 1 Week - 12hr interval
    case 1209600:
    case 2592000:
    case 1:
    case 2:
      reAggInterval = 12 * 3600;
      break;
    default:
      reAggInterval = undefined;
  }

  return {
    ...getBaseAggQuery({ filters, lookback_seconds, allowProjected }),
    fastData: 'Full', // Needs to be Full. Fast downsamples the number of unique IPs
    viz_type: 'stackedBar',
    metric: ['service_name'],
    aggregateTypes: ['p95th_dst_ip'],
    topx: 3,
    depth: 3,
    minsPolling: 1,
    reAggFn: 'avg',
    reAggInterval,
    filterDimensionsEnabled: true,
    filterDimensionName: 'Subscribership',
    filterDimensionOther: false,
    filterDimensionSort: false,
    filterDimensions: {
      connector: 'All',
      filterGroups
    }
  };
};

export const getPerformanceQuery = ({
  service,
  metric,
  filters = [],
  lookback_seconds,
  aggregateType,
  allowProjected = false
}) => ({
  ...getBaseAggQuery({
    filters: filters.concat({
      filterField: 'service_name',
      operator: '=',
      filterValue: service
    }),
    lookback_seconds,
    aggregateType,
    allowProjected
  }),

  fastData: 'Full', // Needs to be Full. Fast downsamples the number of unique IPs
  topx: 20,
  depth: 350,
  viz_type: 'line',
  metric,

  aggregateTypes: [aggregateType]
});

export const getInterfaceTrafficQuery = ({
  lookback_seconds,
  snmp_id,
  device_name,
  aggregateType = 'max_ktappprotocol__snmp__INT64_00',
  viz_type = 'line'
}) => ({
  ...getBaseQuery({
    lookback_seconds,
    aggregateType
  }),
  filters: {
    connector: 'All',
    filterGroups: [
      buildFilterGroup({
        filters: [
          {
            filterField: 'ktappprotocol__snmp__i_device_name',
            operator: '=',
            filterValue: device_name
          },
          {
            filterField: 'ktappprotocol__snmp__output_port',
            operator: '=',
            filterValue: snmp_id
          }
        ]
      })
    ]
  },
  fastData: 'Full',
  topx: 1,
  depth: 1,
  viz_type,
  metric: ['ktappprotocol__snmp__output_port']
});

export const getServiceInterfaceQuery = ({
  lookback_seconds,
  service,
  snmp_id,
  device_name,
  aggregateType,
  allowProjected = false
}) => {
  const filters = [
    {
      filterField: 'i_device_name',
      operator: '=',
      filterValue: device_name
    },
    {
      filterField: 'input_port',
      operator: '=',
      filterValue: snmp_id
    },
    {
      filterField: 'service_name',
      operator: '=',
      filterValue: service
    }
  ];

  return {
    ...getBaseAggQuery({ lookback_seconds, allowProjected }),
    fastData: 'Fast',
    topx: 1,
    depth: 1,
    viz_type: 'line',
    metric: ['InterfaceID_src'],
    aggregateTypes: [aggregateType],
    filterDimensionsEnabled: true,
    filterDimensions: {
      connector: 'All',
      filterGroups: [
        buildFilterGroup({
          name: `${device_name} (${snmp_id})`,
          named: true,
          filters
        })
      ]
    }
  };
};
